
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { Suspense } from 'react';
import { GetStaticPropsContext } from 'next';
import { Makeswift, Page as MakeswiftPage, PageProps as MakeswiftPageProps, ReactRuntimeProvider } from '@makeswift/runtime/next';
import { algoliasearch } from 'algoliasearch';
import { fetchAllFacets } from 'pages/api/algolia/marketplace/apps/all-facets';
import { renderToString } from 'react-dom/server';
import { InstantSearchServerState, getServerState } from 'react-instantsearch';
import { CategoriesDocument } from '@/generated/contentful';
import { client as contentful } from '@/lib/contentful/client';
import { serializeCacheKey } from '@/lib/contentful/utils';
import { getPageSnapshot } from '@/lib/makeswift/client';
import '@/lib/makeswift/components';
import { runtime } from '@/lib/makeswift/runtime';
import { getAlgoliaIndex } from '@/utils/apps/getAlgoliaIndex';
import { ALGOLIA_APPS_HITS_PER_PAGE } from '@/utils/apps/getAppParams';
import { pageLocales } from '@/utils/locales/page-locales';
import { AppCategoryMeta } from '@/components/Contentful/entries/Marketplace/Apps/AppCategoryMeta';
import { AppsProvider } from '@/components/Contentful/entries/Marketplace/Apps/AppsProvider';
const algoliaClient = algoliasearch(process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || '', process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY || '');
type Props = MakeswiftPageProps & {
    serverState?: InstantSearchServerState;
    parentCategorySlug?: string | null;
    childCategorySlug?: string | null;
    renderAlgoliaServerState?: boolean;
    previewMode: boolean;
    locale: string | undefined;
    slug: string;
};
export default function AppsCategoriesPage({ snapshot, serverState, parentCategorySlug, childCategorySlug, locale, previewMode, renderAlgoliaServerState = false, slug }: Props) {
    const page = renderAlgoliaServerState ? (<Suspense>
			<ReactRuntimeProvider runtime={runtime} locale={locale} previewMode={previewMode}>
				<MakeswiftPage snapshot={snapshot}/>
			</ReactRuntimeProvider>
		</Suspense>) : (<MakeswiftPage snapshot={snapshot}/>);
    return (<>
			<AppsProvider serverState={serverState} parentCategory={parentCategorySlug} childCategory={childCategorySlug}>
				<AppCategoryMeta locale={locale} parentCategorySlug={parentCategorySlug} childCategorySlug={childCategorySlug}>
					{page}
				</AppCategoryMeta>
			</AppsProvider>
		</>);
}
async function getStaticProps({ params, previewData, preview, locale }: GetStaticPropsContext<{
    slug: string[] | undefined;
}, {
    makeswift: boolean;
}>) {
    if (!pageLocales.apps?.includes(locale as Locale)) {
        console.info('Locale not supported for apps', locale);
        return { notFound: true, revalidate: 3600 };
    }
    if (params?.slug && params.slug.length > 2) {
        console.info('Slug not supported for apps', params.slug);
        return { notFound: true, revalidate: 3600 };
    }
    const client = contentful({ preview: Boolean(preview) });
    const category = await client.request(CategoriesDocument, {
        where: { slug: params?.slug?.at(-1) },
        limit: 1,
        locale: 'en-US',
        preview: Boolean(preview)
    });
    const snapshot = await getPageSnapshot({
        pathname: '/apps/categories/__template__',
        isMakeswiftPreview: previewData?.makeswift == true,
        previewData,
        locale,
        timeout: {
            ms: 10000,
            message: `Timeout request getPageSnapshot for "/apps/categories/__template__" – Locale: ${locale}`
        }
    });
    if (snapshot == null) {
        console.warn('Could not get snapshot for /apps/categories/__template__');
        return { notFound: true };
    }
    const parentCategorySlug = params?.slug?.at(0) ?? null;
    const childCategorySlug = params?.slug?.at(1) ?? null;
    const slug = `${parentCategorySlug}${childCategorySlug ? `/${childCategorySlug}` : ''}`;
    const initialResults = await algoliaClient.search({
        requests: [
            {
                indexName: process.env.NEXT_PUBLIC_ALGOLIA_MARKETPLACE_APPS_INDEX || '',
                facetFilters: [
                    parentCategorySlug && !childCategorySlug
                        ? `heirarchicalCategories.lvl0:${parentCategorySlug}`
                        : '',
                    parentCategorySlug && childCategorySlug
                        ? `heirarchicalCategories.lvl1:${parentCategorySlug} > ${childCategorySlug}`
                        : ''
                ].filter(Boolean),
                hitsPerPage: ALGOLIA_APPS_HITS_PER_PAGE
            },
            // Gets all facets for categories
            {
                indexName: process.env.NEXT_PUBLIC_ALGOLIA_MARKETPLACE_APPS_INDEX || '',
                facets: ['*'],
                hitsPerPage: ALGOLIA_APPS_HITS_PER_PAGE,
                page: 0
            }
        ]
    });
    const parentCategories = Object.keys(initialResults?.results?.[1]?.facets?.['heirarchicalCategories.lvl0'] ?? {});
    const childCategories = Object.keys(initialResults?.results?.[1]?.facets?.['heirarchicalCategories.lvl1'] ?? {})?.map((combinedCategory) => combinedCategory.split(' > ')[1]);
    if (parentCategorySlug) {
        if (!parentCategories?.some((parentCategory) => parentCategory === parentCategorySlug)) {
            return { notFound: true, revalidate: 3600 };
        }
        if (childCategorySlug) {
            if (!childCategories?.some((childCategory) => childCategory === childCategorySlug)) {
                return { notFound: true, revalidate: 3600 };
            }
        }
    }
    const allAppFacets = await fetchAllFacets();
    const serverState = await getServerState(<AppsCategoriesPage snapshot={snapshot} parentCategorySlug={parentCategorySlug} childCategorySlug={childCategorySlug} locale={locale} slug={slug} previewMode={Makeswift.getPreviewMode(previewData)} renderAlgoliaServerState={true}/>, {
        renderToString
    });
    const algoliaIndex = getAlgoliaIndex('');
    return {
        props: {
            snapshot,
            previewMode: Makeswift.getPreviewMode(previewData),
            fallback: {
                [serializeCacheKey('appCategory', { slug: params?.slug?.at(-1), locale })]: category,
                [serializeCacheKey('appAlgoliaFacets', { locale })]: allAppFacets
            },
            serverState: {
                initialResults: {
                    [algoliaIndex]: {
                        results: initialResults?.results ?? serverState?.initialResults?.[algoliaIndex]?.results
                    }
                } as unknown
            },
            preview: Boolean(preview),
            parentCategorySlug,
            childCategorySlug,
            locale,
            slug
        },
        revalidate: 3600
    };
}
export async function getStaticPaths() {
    return {
        paths: [{ params: { slug: [] } }],
        fallback: 'blocking'
    };
}

    async function __Next_Translate__getStaticProps__1961694aa07__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/apps/categories/[[...slug]]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1961694aa07__ as getStaticProps }
  